'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Heart, Menu, X, ChevronDown, MapPin, Search, User, LogOut, Settings, LayoutDashboard, FolderHeart, MessageCircle, Receipt, Crown, Calculator, Megaphone } from 'lucide-react';

interface HeaderProps {
  transparent?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  agent: 'Emlakçı',
  seller: 'İlan Sahibi',
  user: 'Kullanıcı',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-red-100 text-red-700',
  agent: 'bg-blue-100 text-blue-700',
  seller: 'bg-green-100 text-green-700',
  user: 'bg-purple-100 text-purple-700',
};

export default function Header({ transparent = false }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, profile, loading, signOut } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      router.push('/');
      router.refresh();
    } catch {}
  };

  const navItems = [
    {
      label: 'Satılık',
      href: '/property-search?tip=satilik',
      dropdown: [
        { label: 'Daire', href: '/property-search?tip=satilik&tur=daire' },
        { label: 'Villa', href: '/property-search?tip=satilik&tur=villa' },
        { label: 'Müstakil Ev', href: '/property-search?tip=satilik&tur=mustakil' },
        { label: 'Arsa', href: '/property-search?tip=satilik&tur=arsa' },
        { label: 'İşyeri', href: '/property-search?tip=satilik&tur=isyeri' },
      ],
    },
    {
      label: 'Kiralık',
      href: '/property-search?tip=kiralik',
      dropdown: [
        { label: 'Daire', href: '/property-search?tip=kiralik&tur=daire' },
        { label: 'Villa', href: '/property-search?tip=kiralik&tur=villa' },
        { label: 'Rezidans', href: '/property-search?tip=kiralik&tur=rezidans' },
        { label: 'Ofis', href: '/property-search?tip=kiralik&tur=ofis' },
      ],
    },
    { label: 'Haritada Ara', href: '/property-search?view=map', dropdown: null },
    { label: 'Yatırım', href: '/property-search?yatirim=true', dropdown: null },
    { label: 'Yatırım Hesaplayıcı', href: '/yatirim-hesaplayici', dropdown: null },
    { label: 'Fiyatlandırma', href: '/pricing', dropdown: null },
    { label: 'Emlakçı Bul', href: '/property-search?emlakci=true', dropdown: null },
    {
      label: 'Emlakçı Portalı',
      href: '/agent-portal',
      dropdown: [
        { label: 'Genel Bakış', href: '/agent-portal' },
        { label: 'İlan Yönetimi', href: '/agent-portal/listings' },
        { label: 'Müşteri Adayları', href: '/agent-portal/leads' },
        { label: 'Tur Takvimi', href: '/agent-portal/calendar' },
        { label: 'Performans', href: '/agent-portal/analytics' },
      ],
    },
  ];

  const headerBg =
    transparent && !isScrolled
      ? 'bg-transparent' :'bg-white/95 backdrop-blur-md border-b border-border shadow-sm';

  const textColor = transparent && !isScrolled ? 'text-white' : 'text-foreground';
  const logoColor = transparent && !isScrolled ? 'brightness-0 invert' : '';

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className={logoColor}>
                <AppLogo size={36} />
              </div>
              <span className={`font-extrabold text-xl tracking-tight ${textColor}`}>Evora</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <div
                  key={`nav-${item.label}`}
                  className="relative"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    href={item.href}
                    className={`flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-150 hover:bg-primary-light hover:text-primary ${textColor}`}
                  >
                    {item.label}
                    {item.dropdown && <ChevronDown size={14} className="opacity-60" />}
                  </Link>

                  {item.dropdown && activeDropdown === item.label && (
                    <div className="absolute top-full left-0 mt-1 w-48 card-elevated rounded-xl overflow-hidden animate-fade-in z-50">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={`sub-${sub.label}`}
                          href={sub.href}
                          className="block px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {(!transparent || isScrolled) && (
                <Link
                  href="/property-search"
                  className="hidden md:flex items-center justify-center w-9 h-9 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                >
                  <Search size={18} />
                </Link>
              )}

              <Link
                href="/property-search"
                className={`hidden md:flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
                  transparent && !isScrolled
                    ? 'text-white/80 hover:text-white hover:bg-white/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Heart size={18} />
              </Link>

              <Link
                href="/property-search"
                className={`hidden md:flex items-center justify-center w-9 h-9 rounded-xl transition-colors relative ${
                  transparent && !isScrolled
                    ? 'text-white/80 hover:text-white hover:bg-white/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Bell size={18} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent rounded-full border-2 border-white" />
              </Link>

              {/* Auth Buttons */}
              {!loading && (
                <>
                  {user ? (
                    <div className="relative hidden md:block">
                      <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-all duration-150 ${
                          transparent && !isScrolled
                            ? 'border-white/30 text-white hover:bg-white/10' :'border-border text-foreground hover:bg-secondary'
                        }`}
                      >
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                          {profile?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="max-w-[100px] truncate">
                          {profile?.full_name || user?.email?.split('@')[0]}
                        </span>
                        {profile?.role && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${ROLE_COLORS[profile.role] || 'bg-muted text-muted-foreground'}`}>
                            {ROLE_LABELS[profile.role]}
                          </span>
                        )}
                        <ChevronDown size={14} className="opacity-60" />
                      </button>

                      {userMenuOpen && (
                        <div
                          className="absolute top-full right-0 mt-1 w-52 card-elevated rounded-xl overflow-hidden animate-fade-in z-50"
                          onMouseLeave={() => setUserMenuOpen(false)}
                        >
                          <div className="px-4 py-3 border-b border-border">
                            <p className="text-sm font-bold text-foreground truncate">
                              {profile?.full_name || 'Kullanıcı'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          </div>
                          {(profile?.role === 'agent' || profile?.role === 'admin') && (
                            <Link
                              href="/agent-portal"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                            >
                              <LayoutDashboard size={15} />
                              Emlakçı Portalı
                            </Link>
                          )}
                          <Link
                            href="/koleksiyonlar"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                          >
                            <FolderHeart size={15} />
                            Koleksiyonlarım
                          </Link>
                          <Link
                            href="/mesajlar"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                          >
                            <MessageCircle size={15} />
                            Mesajlarım
                          </Link>
                          <Link
                            href="/profil/odemeler"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                          >
                            <Receipt size={15} />
                            Ödemelerim
                          </Link>
                          {(profile?.role === 'agent') && (
                            <Link
                              href="/profil/abonelik"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                            >
                              <Crown size={15} />
                              Aboneliğim
                            </Link>
                          )}
                          {profile?.role === 'admin' && (
                            <Link
                              href="/admin/odemeler"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                            >
                              <Calculator size={15} />
                              Ödeme Yönetimi
                            </Link>
                          )}
                          {profile?.role === 'admin' && (
                            <Link
                              href="/admin/kampanyalar"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                            >
                              <Megaphone size={15} />
                              Kampanyalar
                            </Link>
                          )}
                          <Link
                            href="/profil"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                          >
                            <User size={15} />
                            Profilim
                          </Link>
                          <Link
                            href="/ayarlar"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                          >
                            <Settings size={15} />
                            Ayarlar
                          </Link>
                          <button
                            onClick={handleSignOut}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-danger hover:bg-danger/10 transition-colors border-t border-border"
                          >
                            <LogOut size={15} />
                            Çıkış Yap
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <Link
                        href="/giris"
                        className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-150 ${
                          transparent && !isScrolled
                            ? 'border-white/30 text-white hover:bg-white/10' :'border-border text-foreground hover:bg-secondary'
                        }`}
                      >
                        Giriş Yap
                      </Link>
                      <Link href="/kayit" className="btn-primary text-sm hidden md:inline-flex">
                        Üye Ol
                      </Link>
                    </>
                  )}
                </>
              )}

              {/* İlan Ver button — only for logged-in agents/sellers/admins */}
              {user && (profile?.role === 'agent' || profile?.role === 'seller' || profile?.role === 'admin') && (
                <Link href="/agent-portal/listings" className="btn-primary text-sm hidden md:inline-flex">
                  İlan Ver
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-xl transition-colors ${
                  transparent && !isScrolled
                    ? 'text-white hover:bg-white/10' :'text-foreground hover:bg-muted'
                }`}
                aria-label="Menüyü aç"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-border animate-slide-up">
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={`mobile-nav-${item.label}`}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-primary-light hover:text-primary transition-colors"
                >
                  <MapPin size={16} className="text-primary" />
                  {item.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-border flex gap-2">
                {user ? (
                  <button
                    onClick={handleSignOut}
                    className="flex-1 btn-secondary text-sm justify-center flex items-center gap-2"
                  >
                    <LogOut size={14} />
                    Çıkış Yap
                  </button>
                ) : (
                  <>
                    <Link
                      href="/giris"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 btn-secondary text-sm justify-center"
                    >
                      Giriş Yap
                    </Link>
                    <Link
                      href="/kayit"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 btn-primary text-sm justify-center"
                    >
                      Üye Ol
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border safe-area-bottom">
        <div className="flex items-center justify-around py-2">
          {[
            {
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              ),
              label: 'Ana Sayfa',
              href: '/',
            },
            { icon: <Search size={20} />, label: 'Ara', href: '/property-search' },
            { icon: <MapPin size={20} />, label: 'Harita', href: '/property-search?view=map' },
            { icon: <Heart size={20} />, label: 'Favoriler', href: '/property-search' },
            {
              icon: user ? (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold">
                  {profile?.full_name?.[0]?.toUpperCase() || 'U'}
                </div>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M20 21a8 8 0 1 0-16 0" />
                </svg>
              ),
              label: user ? 'Profil' : 'Giriş',
              href: user ? '/profil' : '/giris',
            },
          ].map((item) => (
            <Link
              key={`bottom-nav-${item.label}`}
              href={item.href}
              className="flex flex-col items-center gap-1 px-3 py-1 text-muted-foreground hover:text-primary transition-colors"
            >
              {item.icon}
              <span className="text-[10px] font-semibold">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}